import React, { useState, useRef, useCallback, useEffect } from 'react';
import '../../styles/ScanUploadModal.css';

export default function ScanUploadModal({ patient, onClose, onAddToQueue }) {
  const [leftFiles,  setLeftFiles]  = useState([]);
  const [rightFiles, setRightFiles] = useState([]);

  const leftRef  = useRef(null);
  const rightRef = useRef(null);

  /* ── File helpers ────────────────────────────────── */
  const addFiles = async (side, incoming) => {
    const setter  = side === 'left' ? setLeftFiles : setRightFiles;
    const current = side === 'left' ? leftFiles    : rightFiles;
    const merged  = [...current];
    
    for (const f of incoming) {
      // Validate file is .BMP format (both extension and content)
      const fileExtension = f.name.toLowerCase().split('.').pop();
      const isValidBmp = fileExtension === 'bmp';
      
      if (!isValidBmp) {
        alert(`Invalid file format: ${f.name}\n\nOnly .BMP files are allowed for ultrasound images.`);
        continue;
      }
      
      // Additional validation: Check file header to ensure it's a real BMP ultrasound image
      const validateBmpContent = async (file) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const arrayBuffer = e.target.result;
            const bytes = new Uint8Array(arrayBuffer);
            
            // BMP files must start with 'BM' (0x42 0x4D) in the first 2 bytes
            if (bytes.length < 2 || bytes[0] !== 0x42 || bytes[1] !== 0x4D) {
              resolve(false);
              return;
            }
            
            // Read more bytes to detect converted files and check ultrasound patterns
            if (bytes.length >= 200) {
              // Enhanced converted file detection
              const fileString = Array.from(bytes.slice(0, 200)).map(b => String.fromCharCode(b)).join('');
              const hexString = Array.from(bytes.slice(0, 200)).map(b => b.toString(16).padStart(2, '0')).join('');
              
              // Check for JPEG signatures (converted files often retain these)
              if (hexString.includes('ffd8') || hexString.includes('ffe0') || hexString.includes('ffe1')) {
                alert(`Converted image detected: ${f.name}\n\nThis file appears to be converted from JPEG format. Only original BMP ultrasound images are allowed.`);
                resolve(false);
                return;
              }
              
              // Check for PNG signatures
              if (hexString.includes('89504e47') || fileString.includes('PNG')) {
                alert(`Converted image detected: ${f.name}\n\nThis file appears to be converted from PNG format. Only original BMP ultrasound images are allowed.`);
                resolve(false);
                return;
              }
              
              // Check for GIF signatures
              if (hexString.includes('47494638') || fileString.includes('GIF')) {
                alert(`Converted image detected: ${f.name}\n\nThis file appears to be converted from GIF format. Only original BMP ultrasound images are allowed.`);
                resolve(false);
                return;
              }
              
              // Check for common converted file markers
              if (fileString.includes('JFIF') || fileString.includes('Exif') || 
                  fileString.includes('Photoshop') || fileString.includes('Adobe') ||
                  fileString.includes('Created with') || fileString.includes('Generated by')) {
                alert(`Converted image detected: ${f.name}\n\nThis file appears to be converted. Only original BMP ultrasound images are allowed.`);
                resolve(false);
                return;
              }
              
              // Check for TIFF signatures
              if (hexString.includes('49492a00') || hexString.includes('4d4d002a')) {
                alert(`Converted image detected: ${f.name}\n\nThis file appears to be converted from TIFF format. Only original BMP ultrasound images are allowed.`);
                resolve(false);
                return;
              }
              
              // Additional check: Look for metadata that indicates conversion
              if (fileString.includes('Metadata') || fileString.includes('EXIF') || 
                  fileString.includes('XMP') || fileString.includes('IPTC')) {
                alert(`Converted image detected: ${f.name}\n\nThis file contains metadata indicating it was converted. Only original BMP ultrasound images are allowed.`);
                resolve(false);
                return;
              }
              
              // Check for ultrasound image patterns
              const hasUltrasoundPattern = validateUltrasoundPattern(bytes, file.size);
              if (!hasUltrasoundPattern) {
                resolve(false);
                return;
              }
            }
            
            resolve(true);
          };
          reader.onerror = () => resolve(false);
          reader.readAsArrayBuffer(file.slice(0, 300)); // Read first 300 bytes for better converted file detection
        });
      };
      
      // Helper function to validate ultrasound image patterns (more lenient)
      const validateUltrasoundPattern = (bytes, fileSize) => {
        try {
          // Extract BMP header information
          if (bytes.length < 54) return false;
          
          const width = bytes[18] | (bytes[19] << 8) | (bytes[20] << 16) | (bytes[21] << 24);
          const height = bytes[22] | (bytes[23] << 8) | (bytes[24] << 16) | (bytes[25] << 24);
          const bitsPerPixel = bytes[28] | (bytes[29] << 8);
          
          // More lenient ultrasound image validation
          // 1. Check dimensions (wider range for various ultrasound devices)
          if (width < 200 || width > 2000 || height < 200 || height > 2000) {
            return false;
          }
          
          // 2. Check aspect ratio (more flexible for different ultrasound views)
          const aspectRatio = width / height;
          if (aspectRatio < 0.2 || aspectRatio > 5.0) {
            return false;
          }
          
          // 3. Check file size (wider range for different ultrasound resolutions)
          if (fileSize < 50000 || fileSize > 10000000) {
            return false;
          }
          
          // 4. Check bit depth (accept common ultrasound formats)
          if (![1, 4, 8, 16, 24, 32].includes(bitsPerPixel)) {
            return false;
          }
          
          // 5. Check compression (allow some compression for modern ultrasound)
          const compression = bytes[30] | (bytes[31] << 8) | (bytes[32] << 16) | (bytes[33] << 24);
          if (![0, 1, 2].includes(compression)) {
            return false;
          }
          
          // 6. More lenient acceptance - accept most valid BMP files that could be ultrasound
          // Only reject obvious non-ultrasound patterns
          const fileString = Array.from(bytes.slice(0, 150)).map(b => String.fromCharCode(b)).join('');
          
          // Reject obvious non-medical patterns
          const nonMedicalPatterns = ['PHOTO', 'CAMERA', 'SCREENSHOT', 'DESKTOP', 'WALLPAPER'];
          const hasNonMedicalPatterns = nonMedicalPatterns.some(pattern => 
            fileString.toUpperCase().includes(pattern)
          );
          
          if (hasNonMedicalPatterns) {
            return false;
          }
          
          // Accept most valid BMP files that meet basic criteria
          return true;
          
        } catch (error) {
          console.error('Error validating ultrasound pattern:', error);
          // If validation fails, accept the file (be more lenient)
          return true;
        }
      };
      
      const isRealBmp = await validateBmpContent(f);
      if (!isRealBmp) {
        alert(`Invalid BMP file: ${f.name}\n\nPlease upload a valid BMP ultrasound image.\nMake sure the file is:\n• In BMP format (not converted from other formats)\n• Reasonable size and dimensions`);
        continue;
      }
      
      if (!merged.find(x => x.name === f.name && x.size === f.size)) {
        merged.push(f);
      }
    }
    setter(merged);
  };

  const removeFile = (side, idx) => {
    if (side === 'left') setLeftFiles(p => p.filter((_, i) => i !== idx));
    else                 setRightFiles(p => p.filter((_, i) => i !== idx));
  };

  const clearAll = () => { setLeftFiles([]); setRightFiles([]); };

  /* ── Drag & drop ─────────────────────────────────── */
  const handleDrop = useCallback(async (side, e) => {
    e.preventDefault();
    await addFiles(side, Array.from(e.dataTransfer.files));
  }, [leftFiles, rightFiles]); // eslint-disable-line

  const handleBrowse = async (side, e) => await addFiles(side, Array.from(e.target.files));

  /* ── Add to Queue ────────────────────────────────────
     Stages the files on the patient object and directly triggers analysis.
  ─────────────────────────────────────────────────── */
  const handleAddToQueue = () => {
    if (!leftFiles.length && !rightFiles.length) return;
    onAddToQueue({
      ...patient,
      leftFiles,
      rightFiles,
      status: 'pending',
    });
  };

  const canAdd = leftFiles.length > 0 && rightFiles.length > 0;

  /* ── Thumbnail grid ──────────────────────────────── */
  const FileGrid = ({ files, side }) => (
    <div className="sum__file-grid">
      {files.map((f, i) => (
        <div key={i} className="sum__thumb-wrap">
          <img src={URL.createObjectURL(f)} alt={f.name} className="sum__thumb" />
          <button
            className="sum__thumb-remove"
            onClick={e => { e.stopPropagation(); removeFile(side, i); }}
            title="Remove"
          >
            <i className="fa-solid fa-circle-xmark" />
          </button>
        </div>
      ))}
    </div>
  );

  /* ── Drop zone ───────────────────────────────────── */
  const DropZone = ({ side, files, inputRef: ref }) => (
    <div
      className={`sum__dropzone ${files.length ? 'sum__dropzone--has-files' : ''}`}
      onDragOver={e => e.preventDefault()}
      onDrop={e => handleDrop(side, e)}
      onClick={() => !files.length && ref.current?.click()}
    >
      <input
        ref={ref}
        type="file"
        accept=".bmp,image/bmp"
        multiple
        className="d-none"
        onChange={e => handleBrowse(side, e)}
      />
      {files.length === 0 ? (
        <div className="text-center text-muted py-3">
          <i className="fa-solid fa-cloud-arrow-up fs-3 mb-2 d-block sum__upload-icon" />
          <span className="small sum__upload-text">
            Drag and drop or{' '}
            <span
              className="sum__browse-link"
              onClick={e => { e.stopPropagation(); ref.current?.click(); }}
            >
              browse
            </span>
            {' '}to upload .BMP ultrasound images only
          </span>
          <div className="small text-muted mt-1 sum__upload-subtext">
            BMP, up to 5MB
          </div>
        </div>
      ) : (
        <>
          <FileGrid files={files} side={side} />
          {/* Allow adding more */}
          <div
            className="sum__add-more"
            onClick={e => { e.stopPropagation(); ref.current?.click(); }}
          >
            <i className="fa-solid fa-plus me-1" />Add more
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="sum__overlay">
      <div className="sum__modal-container">

        {/* Background */}
        <div className="sum__bg" />
        <div className="sum__bg-tint" />

        <div className="sum__content">

          {/* Header */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <h5 className="fw-bold mb-0 sum__title">Scan Upload</h5>
            <button
              className="sum__close-btn"
              onClick={onClose}
              aria-label="Close — patient will NOT be added to queue"
              title="Close — patient will NOT be added to queue"
            >
              X
            </button>
          </div>

          {/* Patient info banner — shows ID + Name only */}
          <div className="sum__patient-banner mb-3">
            <span>Patient ID: <strong>{patient.id}</strong></span>
            <span>Patient Name: <strong>{patient.name || '—'}</strong></span>
          </div>

          {/* Hint */}
          <p className="sum__hint mb-3">
            <i className="fa-solid fa-circle-info me-1" />
            Upload one left and one right scan, then click <strong>Start Analyse</strong> to immediately run the AI analysis.
          </p>

          {/* Upload label */}
          <p className="fw-bold mb-3 sum__title">Upload a scan image*</p>

          {/* Two drop zones */}
          <div className="row g-3 mb-3">
            <div className="col-12 col-md-6">
              <DropZone side="right" files={rightFiles} inputRef={rightRef} />
              <p className="text-center fw-semibold mt-2 sum__subtitle">Right Breast Image</p>
            </div>
            <div className="col-12 col-md-6">
              <DropZone side="left"  files={leftFiles}  inputRef={leftRef} />
              <p className="text-center fw-semibold mt-2 sum__subtitle">Left Breast Image</p>
            </div>
          </div>

          {/* Actions */}
          <div className="d-flex justify-content-end gap-2 mt-2">
            <button
              className="btn sum__btn-clear px-4"
              onClick={clearAll}
              disabled={!leftFiles.length && !rightFiles.length}
            >
              Clear
            </button>
            <button
              className="btn sum__btn-start px-4"
              onClick={handleAddToQueue}
              disabled={!canAdd}
            >
              Start Analyse
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}
